// src/pages/module2/Checkin.js

import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import usePhaseFlow from "../../hooks/usePhaseFlow";
import LogoTransition from "../../components/LogoTransition";
import { API_BASE } from "../../api";

const PHASE_QUESTIONS = {
  1: "Ask yourself:\n\nHow am I actually showing up toward my partner right now — more open, more guarded, or more distant?",
  2: "Ask yourself:\n\nWhat emotion do I most often bring into contact with my partner lately, and how does it shape our interaction?",
  3: "Ask yourself:\n\nWhere am I holding back my truth in order to keep peace, and what does that cost me?",
  4: "Ask yourself:\n\nWhat kind of closeness or sexual contact do I want from my partner right now, and what do I avoid saying or doing to protect myself?",
  5: "Ask yourself:\n\nIf I’m honest with myself, what am I hoping this relationship will become — and what am I afraid it won’t?"
};

export default function Checkin() {

  const {
    phase,
    step,
    answer,
    setAnswer,
    submitStep,
    goNext,
    status,
    isLoading,
    showTransition
  } = usePhaseFlow();

  const navigate = useNavigate();
  const { sessionId, role } = useParams();

  const [aiMessage, setAiMessage] = useState("");
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const [typedText, setTypedText] = useState("");
  const [charIndex, setCharIndex] = useState(0);

  const questionText = PHASE_QUESTIONS[phase] || "";

  const [typedAi, setTypedAi] = useState("");
  const [aiCharIndex, setAiCharIndex] = useState(0);

  useEffect(() => {
    setAiMessage("");
    setHasSubmitted(false);
    setTypedText("");
    setCharIndex(0);
    setTypedAi("");
    setAiCharIndex(0);
  }, [phase, step, status]);

  useEffect(() => {

    if (step !== 1 || hasSubmitted || !questionText) return;

    if (charIndex < questionText.length) {

      const t = setTimeout(() => {
        setTypedText(prev => prev + questionText[charIndex]);
        setCharIndex(i => i + 1);
      }, 28);

      return () => clearTimeout(t);
    }

  }, [charIndex, questionText, step, hasSubmitted]);

  useEffect(() => {

    if (!aiMessage) return;

    if (aiCharIndex < aiMessage.length) {

      const t = setTimeout(() => {
        setTypedAi(prev => prev + aiMessage[aiCharIndex]);
        setAiCharIndex(i => i + 1);
      }, 18);

      return () => clearTimeout(t);
    }

  }, [aiCharIndex, aiMessage]);

  useEffect(() => {

    if (status !== "waiting") return;

    const checkBridge = async () => {

      const res = await fetch(`${API_BASE}/module2/phase/bridge/${sessionId}`
      
      );

      const data = await res.json();

      if (data?.bridge) {
        navigate(`/module2/bridge/${sessionId}/${role}/${phase}`);
      }

    };

    const t = setInterval(checkBridge, 2000);

    return () => clearInterval(t);

  }, [status, sessionId, role, phase, navigate]);

  async function handleSubmit() {

    if (!hasSubmitted) {

      const res = await submitStep();

      if (res?.ai_message) {
        setAiMessage(res.ai_message);
        setHasSubmitted(true);
      }

      return;
    }

    goNext();
  }

  if (status === "finished") {

    const completed =
      JSON.parse(
        sessionStorage.getItem(`completed_phases_${sessionId}_${role}`)
      ) || [];

    if (!completed.includes(phase)) {

      sessionStorage.setItem(
        `completed_phases_${sessionId}_${role}`,
        JSON.stringify([...completed, phase])
      );
    }

    return (
      <div
        style={{
          padding: 120,
          textAlign: "center",
          color: "#F5F4ED"
        }}
      >
        <h2>Class complete.</h2>
      </div>
    );
  }

  return (

    <div
      className="fade-in"
      style={{
        minHeight: "100vh",
        paddingTop: 80,

        background:
          "radial-gradient(circle at 50% 45%, #123044 0%, #081822 55%, #020608 100%)"
      }}
    >

      {showTransition && <LogoTransition />}

      {!showTransition && (

        <div
          key={phase}
          className="card"
          style={{
            maxWidth: 860,
            margin: "0 auto",

            background: "rgba(18,48,68,0.35)",
            border: "1px solid rgba(245,244,237,0.25)",

            backdropFilter: "blur(8px)",

            borderRadius: 22,

            boxShadow: "0 0 20px rgba(0,0,0,0.25)"
          }}
        >

          {status === "active" && step === 1 && !hasSubmitted && (

            <p
              style={{
                fontFamily: "Cormorant Garamond, serif",
                fontSize: 16,
                lineHeight: 1.9,
                letterSpacing: "0.04em",

                color: "#F5F4ED",

                whiteSpace: "pre-wrap",

                marginBottom: 32
              }}
            >
              {typedText}
            </p>

          )}

          {typedAi && status === "active" && (

            <div
              style={{
                background: "rgba(2,6,8,0.55)",

                padding: 16,

                marginBottom: 24,

                lineHeight: 1.7,

                borderRadius: 14,

                color: "#F5F4ED",

                whiteSpace: "pre-wrap",

                border: "1px solid rgba(245,244,237,0.15)"
              }}
            >
              {typedAi}
            </div>

          )}

          {status === "active" &&
            (!hasSubmitted ||
              (hasSubmitted && aiCharIndex >= aiMessage.length)) && (

              <>

                <textarea
                  className="input"
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  rows={23}
                />

                <div style={{ marginTop: 24, textAlign: "right" }}>

                  <button
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={isLoading || (!answer.trim() && !hasSubmitted)}
                  >
                    {hasSubmitted ? "Continue" : "Submit"}
                  </button>

                </div>

              </>
            )}

          {status === "waiting" && (

            <p
              className="hint"
              style={{
                marginTop: 24,
                color: "#F5F4ED"
              }}
            >
              Waiting for your partner…
            </p>

          )}

        </div>

      )}

    </div>
  );
} 
